import { createAction, createReducer } from '@reduxjs/toolkit';

import api from 'common/api';
import { AppState } from 'common/models/AppState';
import { RegistrationFormValues, UserProfile, ProfileFormValues } from 'common/models/User';

import { startListening } from '../../../../root/store/listener-middleware';
import { selectAuth } from '../../ducks';

// Actions
export const registrationActions = {
  request: createAction<{ auth: RegistrationFormValues; profile: ProfileFormValues }>('@@Registration/REQUEST'),
  success: createAction<UserProfile>('@@Registration/SUCCESS'),
  failure: createAction('@@Registration/FAILURE'),
};

export const activateRegistrationActions = {
  request: createAction<string>('@@Registration_Activation/REQUEST'),
  success: createAction('@@Registration_Activation/SUCCESS'),
  failure: createAction('@@Registration_Activation/FAILURE'),
};

export const resetRegistrationActivationAction = createAction('@@Registration_Activation/RESET');

export const registrationResetAction = createAction('@@Registration/RESET');

// Reducers
export interface RegistrationState {
  error: boolean;
  loading: boolean;
  success: boolean;
}

const initialState: RegistrationState = {
  error: false,
  loading: false,
  success: false,
} as const;

export default createReducer(initialState, (builder) => {
  builder
    .addCase(registrationActions.failure, (state) => {
      state.error = true;
    })
    .addCase(activateRegistrationActions.failure, (state) => {
      state.error = true;
    })
    .addCase(registrationActions.success, (state) => {
      state.success = true;
    })
    .addCase(activateRegistrationActions.success, (state) => {
      state.success = true;
    })
    .addCase(registrationResetAction, () => initialState)
    .addCase(resetRegistrationActivationAction, () => initialState);
});

// Selectors
export function selectRegistration(state: AppState) {
  return selectAuth(state).registration;
}

export function selectRegistrationSuccess(state: AppState) {
  return selectRegistration(state).success;
}

// Listeners
startListening({
  actionCreator: registrationActions.request,
  effect: async (action, listenerApi) => {
    listenerApi.cancelActiveListeners();
    const resp = await api.user.register(action.payload.auth, action.payload.profile);

    if (resp.ok) {
      listenerApi.dispatch(registrationActions.success(resp.data));
    } else {
      listenerApi.dispatch(registrationActions.failure());
    }
  },
});

startListening({
  actionCreator: activateRegistrationActions.request,
  effect: async (action, listenerApi) => {
    listenerApi.cancelActiveListeners();
    const resp = await api.user.activateRegistration(action.payload);

    if (resp.ok) {
      listenerApi.dispatch(activateRegistrationActions.success());
    } else {
      listenerApi.dispatch(activateRegistrationActions.failure());
    }
  },
});
